import { type AttachmentFileType, type FileId } from "@inrev/common";
import type { QueryKey } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { v4 } from "uuid";
import { useGetFileUrl } from "../../api";
import type { UploadFile } from "../../types";
import { getNameAndExtensionFromFileName } from "../../utils";
import { useAttachmentUpload } from "../bond-request/agent";

const UPLOAD_SUCCESS_DISPLAY_MS = 1250;

export type AttachmentActionsConfig = {
	downloadBaseUrl: string;
	downloadQueryKeyPrefix: string;
	allowDelete: boolean;
	allowUpload: boolean;
	allowTypeConfirmation: boolean;
	onDeleteAttachment: (attachmentId: string) => void;
	deletingAttachmentId: string | null;
	updateAttachmentTypes: ReturnType<typeof useAttachmentUpload<AttachmentFileType>>["updateFn"];
	handleUpload: (files: File[]) => void;
	handleDownload: (attachmentId: string) => void;
	filesPendingUpload: UploadFile<AttachmentFileType>[];
	filesCompletedUpload: UploadFile<AttachmentFileType>[];
};

type UseAttachmentActionsConfig = {
	uploadUrl: string;
	downloadBaseUrl: string;
	downloadQueryKeyPrefix: string;
	invalidateQueryKeys?: QueryKey[];
	allowDelete: boolean;
	allowUpload: boolean;
	allowTypeConfirmation: boolean;
};

export function useAttachmentActions({
	uploadUrl,
	downloadBaseUrl,
	downloadQueryKeyPrefix,
	invalidateQueryKeys,
	allowDelete,
	allowUpload,
	allowTypeConfirmation,
}: UseAttachmentActionsConfig): AttachmentActionsConfig {
	const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
	const [filesPendingUpload, setFilesPendingUpload] = useState<UploadFile<AttachmentFileType>[]>(
		[],
	);
	const [filesCompletedUpload, setFilesCompletedUpload] = useState<
		UploadFile<AttachmentFileType>[]
	>([]);

	const {
		updateFn: updateAttachmentTypes,
		deleteFn: deleteAttachment,
		uploadFn,
	} = useAttachmentUpload<AttachmentFileType>(
		uploadUrl,
		{
			onUploadSuccess: (_data, args) => {
				const completed = args.fileUploads.map((u) => u.pendingFile);
				setFilesPendingUpload((pending) =>
					pending.filter(
						(file) => !args.fileUploads.some((upload) => file.id === upload.pendingFile.id),
					),
				);
				setFilesCompletedUpload((prev) => [...prev, ...completed]);
				setTimeout(
					() =>
						setFilesCompletedUpload((prev) =>
							prev.filter((f) => !completed.some((c) => c.id === f.id)),
						),
					UPLOAD_SUCCESS_DISPLAY_MS,
				);
			},
			onUploadError: (_error, args) => {
				setFilesPendingUpload((pending) =>
					pending.filter(
						(file) => !args.fileUploads.some((upload) => file.id === upload.pendingFile.id),
					),
				);
			},
			onUpdateSuccess: () => {},
			onUpdateError: () => {},
			onDeleteSuccess: () => {
				setDeletingAttachmentId(null);
			},
			onDeleteError: () => {
				setDeletingAttachmentId(null);
			},
		},
		invalidateQueryKeys,
	);

	const onDeleteAttachment = useCallback(
		(id: string) => {
			setDeletingAttachmentId(id);
			deleteAttachment({ id });
		},
		[deleteAttachment],
	);

	const handleUpload = useCallback(
		(dropZoneFiles: File[]) => {
			const fileUploads = dropZoneFiles.map((file) => {
				const { fileName, extension } = getNameAndExtensionFromFileName(file.name);
				return {
					rawFile: file,
					pendingFile: {
						id: v4() as FileId,
						name: fileName,
						extension,
						types: [],
					},
				};
			});
			setFilesPendingUpload((pending) => [...fileUploads.map((u) => u.pendingFile), ...pending]);
			uploadFn({ fileUploads });
		},
		[uploadFn],
	);

	const { getFileUrl } = useGetFileUrl();

	const handleDownload = useCallback(
		(attachmentId: string) => {
			getFileUrl({
				baseUrl: `${downloadBaseUrl}/${attachmentId}`,
				queryKey: [downloadQueryKeyPrefix, attachmentId, true],
				asDownload: true,
			});
		},
		[getFileUrl, downloadBaseUrl, downloadQueryKeyPrefix],
	);

	return useMemo(
		(): AttachmentActionsConfig => ({
			downloadBaseUrl,
			downloadQueryKeyPrefix,
			allowDelete,
			allowUpload,
			allowTypeConfirmation,
			onDeleteAttachment,
			deletingAttachmentId,
			updateAttachmentTypes,
			handleUpload,
			handleDownload,
			filesPendingUpload,
			filesCompletedUpload,
		}),
		[
			downloadBaseUrl,
			downloadQueryKeyPrefix,
			allowDelete,
			allowUpload,
			allowTypeConfirmation,
			onDeleteAttachment,
			deletingAttachmentId,
			updateAttachmentTypes,
			handleUpload,
			handleDownload,
			filesPendingUpload,
			filesCompletedUpload,
		],
	);
}
